import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { isNil } from 'lodash'
import { Card, CardContent } from '@mui/material'
import CartesianEChart from '../../../components/echart/CartesianEChart'
import { arrayOf } from '../../../utils/StoreUtils'
import { getExecStatusColor, getExecStatusIconName, getExecStatusLabel } from '../../../utils/JobUtils'
import RefJobAction from '../../../domain/job/RefJobAction'
import Job from '../../dto/DtoJob'

const SELECTED_COLOR = '#26a69a'
const ICON_RENDER_SIZE = 48
const MATERIAL_ICONS_FONT = 'Material Icons'

// Rend le glyphe Material Icons (même police et mêmes noms d'icônes que getExecStatusIcon)
// en image data-URI, utilisable comme symbole ECharts. Mémoïsé par (icône + couleur + warning).
// withWarning superpose un petit warning orange (cas « succès avec warnings », statut 2).
const iconSymbolCache = {}
const getStatusIconSymbol = (iconName, color, withWarning = false) => {
    const cacheKey = `${iconName}|${color}|${withWarning}`
    if (iconSymbolCache[cacheKey]) {
        return iconSymbolCache[cacheKey]
    }
    const canvas = document.createElement('canvas')
    canvas.width = ICON_RENDER_SIZE
    canvas.height = ICON_RENDER_SIZE
    const ctx = canvas.getContext('2d')
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    ctx.font = `${ICON_RENDER_SIZE}px '${MATERIAL_ICONS_FONT}'`
    ctx.fillStyle = color
    ctx.fillText(iconName, ICON_RENDER_SIZE / 2, ICON_RENDER_SIZE / 2)
    if (withWarning) {
        const warningSize = ICON_RENDER_SIZE / 2
        ctx.font = `${warningSize}px '${MATERIAL_ICONS_FONT}'`
        ctx.fillStyle = 'orange'
        ctx.fillText('warning', ICON_RENDER_SIZE - warningSize / 2, ICON_RENDER_SIZE - warningSize / 2)
    }
    const symbol = `image://${canvas.toDataURL()}`
    iconSymbolCache[cacheKey] = symbol
    return symbol
}

const JobChainGraph = ({
    chain,
    onClick,
    jobs,
    selectedJob,
}) => {
    const STEP_X = 180
    const STEP_Y = 100

    // Dernière exécution de chaque job de la chaîne (jobId -> exécution), pour afficher son icône de statut.
    const [lastExecutions, setLastExecutions] = useState({})
    // Les glyphes icône sont dessinés sur canvas : on attend que la police Material Icons soit prête.
    const [fontReady, setFontReady] = useState(!!document.fonts?.check?.(`16px '${MATERIAL_ICONS_FONT}'`))

    useEffect(() => {
        if (fontReady || !document.fonts?.ready) {
            return
        }
        document.fonts.ready.then(() => setFontReady(true))
    }, [fontReady])

    useEffect(() => {
        if (!chain.length) {
            setLastExecutions({})
            return
        }
        Promise.all(chain.map(id =>
            RefJobAction.promiseJobExecutions(id, 1)
                .then(([lastExec]) => [id, lastExec])
                .catch(() => [id, undefined]),
        )).then(entries => setLastExecutions(Object.fromEntries(entries)))
    }, [chain])

    const data = chain
        .map((id, index) => {
            const job = jobs.find(j => j.id === id)

            if (!job) {
                return null
            }

            const isSelected = selectedJob && selectedJob.id === id
            const lastExecution = lastExecutions[id]
            const hasExecution = !isNil(lastExecution)
            const showIcon = hasExecution && fontReady
            const withWarning = hasExecution && (lastExecution.status ?? lastExecution.statusCode) === 2

            return {
                id: job.id.toString(),
                name: job.name || '???',
                job,
                statusLabel: hasExecution ? getExecStatusLabel(lastExecution) : undefined,
                symbol: showIcon
                    ? getStatusIconSymbol(getExecStatusIconName(lastExecution), getExecStatusColor(lastExecution), withWarning)
                    : 'circle',
                symbolSize: isSelected ? 34 : 28,

                value: [
                    index * STEP_X,
                    index * STEP_Y,
                ],

                label: {
                    show: true,
                    position: 'right',
                    color: isSelected ? SELECTED_COLOR : 'black',
                    fontWeight: isSelected ? 'bold' : 'normal',
                    formatter: params => {
                        return params.data.name
                    },
                },

                itemStyle: {
                    // Couleur du rond de repli (l'icône image porte déjà sa couleur de statut).
                    color: hasExecution ? getExecStatusColor(lastExecution) : '#bdbdbd',
                    borderColor: isSelected ? SELECTED_COLOR : 'transparent',
                    borderWidth: isSelected ? 2 : 0,
                },
            }
        })
        .filter(Boolean)

    const links = chain
        .map((id, index) => {
            if (!chain[index + 1]) {
                return null
            }
            return {
                source: id.toString(),
                target: chain[index + 1].toString(),
            }
        })
        .filter(Boolean)

    const options = {
        tooltip: {
            trigger: 'item',
            formatter: params => {
                const { name, statusLabel } = params.data ?? {}
                if (!name) {
                    return ''
                }
                return statusLabel ? `${name}<br/>${statusLabel}` : name
            },
        },

        series: [
            {
                type: 'graph',
                layout: 'none',
                coordinateSystem: 'cartesian2d',
                data,
                links,
                roam: true,
                edgeSymbol: [
                    'circle',
                    'arrow',
                ],
                edgeSymbolSize: [
                    4,
                    10,
                ],
                lineStyle: {
                    color: '#888',
                    width: 2,
                },
                label: { show: true },
            },
        ],

        xAxis: {
            type: 'value',
            show: false,
            min: -100,
            max: chain.length * STEP_X + 100,
        },


        yAxis: {
            type: 'value',
            show: false,
            min: -100,
            max: chain.length * STEP_Y + 100,
            inverse: false,
        },
    }

    return (
        <Card>
            <CardContent>
                <CartesianEChart
                    id='jobGraph'
                    options={options}
                    height={500}
                    onClick={event => {
                        if (event.data?.job) {
                            onClick(event.data.job)
                        }
                    }}
                />
            </CardContent>
        </Card>
    )
}

JobChainGraph.propTypes = {
    chain: PropTypes.arrayOf(PropTypes.number),
    jobs: arrayOf(Job),
    selectedJob: PropTypes.object,
    onClick: PropTypes.func,
}

JobChainGraph.defaultProps = {
    chain: [],
    jobs: [],
    selectedJob: null,
    onClick: () => {},
}

export default JobChainGraph